import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthLayout } from '../../components/AuthLayout';
import { Label } from '@werkstatt/ui';
import { Alert, AlertDescription } from '@werkstatt/ui';
import { CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
      setEmail('');
    } catch (err) {
      const detail = isAxiosError(err) ? err.response?.data?.detail : undefined;
      setError(detail || 'Anfrage fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Passwort vergessen"
      subtitle="Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen."
      footer={<p>Werkstatt-Connect © 2026</p>}
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-300">
              Wenn die E-Mail-Adresse registriert ist, wurde eine E-Mail gesendet.
              Bitte überprüfen Sie Ihr Postfach.
            </AlertDescription>
          </Alert>
        )}

        <div>
          <Label htmlFor="email">E-Mail-Adresse</Label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 block w-full px-3 py-2 border border-border rounded-lg shadow-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
            placeholder="you@example.com"
            autoComplete="email"
          />
        </div>

        <div className="space-y-3">
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Wird gesendet…' : 'Link zum Zurücksetzen senden'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full flex justify-center py-2.5 px-4 border border-border rounded-lg text-sm font-medium text-foreground bg-background hover:bg-muted focus:outline-none transition-colors"
          >
            Zurück zur Anmeldung
          </button>
        </div>
      </form>
    </AuthLayout>
  );
}
